'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import s from './index.module.scss';

import { Markdown } from '#/components/skills-page/markdown';

type FaqItem = {
  answer: string;
  id: string;
  question: string;
};

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'what-is-agentic-hub',
    question: 'What is the Agentic Hub?',
    answer:
      "The Agentic Hub is TikTok for Business's one-stop marketplace where advertisers, agencies, and developers discover, install, and use AI Skills, MCP servers, and Agents that automate TikTok advertising workflows: from campaign creation and diagnosis to reporting and creative management.",
  },
  {
    id: 'difference-skill-mcp-agent',
    question: "What's the difference between a Skill, an MCP server, and an Agent?",
    answer: `- **Skill:** a packaged workflow or instruction set (optionally with executable scripts) that teaches an Agent how to perform a TikTok Ads task.
- **MCP server:** a connection layer that exposes TikTok Ads capabilities (or a partner's capabilities) as tools an Agent can call.
- **Agent:** an AI assistant that performs tasks end-to-end. You can use TikTok Ads Manager Ad Assistant (TikTok's first-party agent) or bring your own (e.g., Claude, ChatGPT, Gemini).`,
  },
  {
    id: 'need-mcp-server-for-skills',
    question: "Do I need to have TikTok's MCP Server to use the skills?",
    answer: `It depends on where you run the skill:

- **Using TikTok Ads Manager Ad Assistant:** no setup needed — skills installed from the Agentic Hub run natively, with TikTok capabilities built in.
- **Using your own Agent (Claude, ChatGPT, Gemini, etc.):** skills that act on your TikTok ad account (creating campaigns, pulling reports, updating creatives) require you to connect the [TikTok for Business MCP Server](https://business-api.tiktok.com/portal/docs/tiktok-ads-mcp-server/v1.3) to your Agent. Skills that are purely advisory (guidance or analysis of data you provide) can run without it.

In short, the MCP Server is what lets a skill actually do things in your TikTok ad account from a third-party Agent. Connecting to the MCP Server is more recommended for the full experience.`,
  },
  {
    id: 'how-install-and-use',
    question: 'How do I install and use a skill?',
    answer: `- **Bring your own Agent:** Find and download the skill in the Agentic Hub, and add the skill to your agent.
- **Use it with TikTok Ad Assistant:** Find the skill in the Agentic Hub, click install and you will be redirected to TikTok Ads Manager Ad Assistant to use it.`,
  },
  {
    id: 'is-skill-safe',
    question: 'Is the skill safe?',
    answer:
      'Every third-party skill submitted to the Hub goes through a security audit against our publication standards before it can be published.',
  },
  {
    id: 'how-partners-benefit',
    question: 'How do partners benefit from joining the Hub?',
    answer:
      'The Hub provides distribution to TikTok advertisers, developer profiles for discovery, and a path to monetization, alongside co-marketing and incentive programs.',
  },
];

const DEFAULT_EXPANDED_ITEM_ID = FAQ_ITEMS[0]?.id || null;

export function LandingFaq() {
  const t = useTranslations();
  const [expandedItemId, setExpandedItemId] = useState<string | null>(DEFAULT_EXPANDED_ITEM_ID);

  const handleItemToggle = (itemId: string) => {
    setExpandedItemId((currentId) => (currentId === itemId ? null : itemId));
  };

  return (
    <section className={s.section}>
      <div className={s.inner}>
        <div className={s.headerRow}>
          <h2>{t('landing_faq_title')}</h2>
          <a
            className={s.helpCenterButton}
            href='https://ads.tiktok.com/athena/requester/form/1868927384832145?board_id=1705151646989990&category_1_id=1868982964366401&identify_key=b25033ef7782d41050cbac74f751d547e9d8ad35a39a541a0f0efbbd51fe722f&isFromBoard=true'
            rel='noreferrer'
            target='_blank'
          >
            {t('landing_faq_help_center_cta')}
          </a>
        </div>

        <div className={s.items}>
          {FAQ_ITEMS.map((item) => {
            const isExpanded = item.id === expandedItemId;
            const panelId = `landing-faq-panel-${item.id}`;
            const triggerId = `landing-faq-trigger-${item.id}`;

            return (
              <div className={s.item} key={item.id}>
                <button
                  aria-controls={panelId}
                  aria-expanded={isExpanded}
                  className={s.itemTrigger}
                  id={triggerId}
                  type='button'
                  onClick={() => handleItemToggle(item.id)}
                >
                  <span>{item.question}</span>
                  <span className={isExpanded ? s.chevronUp : s.chevronDown} aria-hidden>
                    <svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
                      <path
                        d='M4 10L8 6L12 10'
                        stroke='currentColor'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='1.6'
                      />
                    </svg>
                  </span>
                </button>

                {isExpanded ? (
                  <div
                    aria-labelledby={triggerId}
                    className={s.itemPanel}
                    id={panelId}
                    role='region'
                  >
                    <Markdown className={s.answerRichText}>{item.answer}</Markdown>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
